import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, Building2, ArrowLeft, CheckCircle2, AlertCircle } from 'lucide-react'
import { Button, Input } from '../atoms'
import { useForgotPassword } from '../../hooks/usePasswordReset'
import { useErrorToast } from '../atoms/Toaster'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submittedEmail, setSubmittedEmail] = useState('')
  const [devResetUrl, setDevResetUrl] = useState<string | null>(null)

  const forgotMutation = useForgotPassword()
  const showError = useErrorToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setDevResetUrl(null)
    try {
      const result = await forgotMutation.mutateAsync(email)
      setSubmittedEmail(email)
      setSubmitted(true)
      if (result.devResetUrl) setDevResetUrl(result.devResetUrl)
    } catch (error: any) {
      showError('Error', error.message || 'No se pudo procesar la solicitud')
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Solicitud enviada</h2>
            <p className="mt-2 text-sm text-gray-600">
              Si el correo <span className="font-medium text-gray-800">{submittedEmail}</span> está registrado, recibirás un enlace para restablecer tu contraseña.
            </p>
            <p className="mt-1 text-xs text-gray-500">El enlace expira en 1 hora.</p>
          </div>

          {devResetUrl && (
            <div className="card p-4 bg-amber-50 border-amber-200 text-left">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-amber-800">Modo desarrollo</p>
                  <p className="text-xs text-amber-700 mt-1">
                    El servicio de email no está configurado. Usa este enlace directamente:
                  </p>
                  <a
                    href={devResetUrl}
                    className="block mt-2 text-xs font-mono text-amber-900 break-all bg-amber-100 px-2 py-1.5 rounded hover:bg-amber-200"
                  >
                    {devResetUrl}
                  </a>
                </div>
              </div>
            </div>
          )}

          <div className="card p-6 space-y-3">
            <Link
              to={`/reset-password${devResetUrl ? `?token=${encodeURIComponent(devResetUrl.split('token=')[1] || '')}` : ''}`}
              className="w-full btn-primary justify-center"
            >
              Continuar al restablecimiento
            </Link>
            <Link
              to="/login"
              className="w-full text-sm text-primary-600 hover:text-primary-700 flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <Building2 className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">¿Olvidaste tu contraseña?</h2>
          <p className="mt-2 text-sm text-gray-600">
            Ingresa tu correo electrónico y te enviaremos un enlace para restablecerla.
          </p>
        </div>

        <form className="card p-6 space-y-4" onSubmit={handleSubmit}>
          <Input
            label="Correo electrónico"
            type="email"
            placeholder="tu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={forgotMutation.isPending}
            leftIcon={<Mail className="w-4 h-4 text-gray-400" />}
          />

          <Button
            type="submit"
            className="w-full"
            isLoading={forgotMutation.isPending}
            disabled={!email}
          >
            Enviar enlace de restablecimiento
          </Button>

          <Link
            to="/login"
            className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
          </Link>
        </form>
      </div>
    </div>
  )
}
